import { z } from "zod";

// Wire formats from the Bolta OpenAPI spec (https://docs.bolta.io/openapi.yaml).
// Field names must match the API exactly; amounts (supplyCost, tax, unitPrice)
// are KRW integers per the spec.

export const BoltaDateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const BoltaBusinessRegistrationNumberSchema = z
  .string()
  .regex(/^\d{10}$/, "Expected a 10-digit 사업자등록번호 without hyphens");

// 사업자등록번호 (10 digits), 주민등록번호 or 외국인등록번호 (13 digits), no hyphens.
export const BoltaRecipientIdentificationNumberSchema = z
  .string()
  .regex(/^\d{10}(\d{3})?$/, "Expected 10 or 13 digits without hyphens");

// 4-digit 종사업장번호, "0000" excluded.
export const BoltaTaxRegistrationIdSchema = z
  .string()
  .regex(/^(?!0{4})\d{4}$/, "Expected a 4-digit 종사업장번호 (not 0000)");

export const BoltaTaxTypeSchema = z.enum(["TAXABLE", "ZERO_RATE", "TAX_FREE"]);
export type BoltaTaxType = z.infer<typeof BoltaTaxTypeSchema>;

export const BoltaPurposeSchema = z.enum(["RECEIPT", "CLAIM"]);
export type BoltaPurpose = z.infer<typeof BoltaPurposeSchema>;

export const BoltaManagerSchema = z.object({
  email: z.string().email().max(40).describe("Manager email address."),
  name: z
    .string()
    .min(1)
    .max(30)
    .nullish()
    .describe("Manager name (optional)."),
  telephone: z
    .string()
    .regex(/^010-\d{3,4}-\d{4}$/)
    .nullish()
    .describe(
      "Mobile number in 010-XXXX-XXXX format. When provided, the manager " +
        "receives a KakaoTalk notification (알림톡)."
    ),
});

export const BoltaSupplierPartySchema = z.object({
  identificationNumber: BoltaBusinessRegistrationNumberSchema.describe(
    "Supplier's 사업자등록번호 (10 digits, no hyphens)."
  ),
  taxRegistrationId: BoltaTaxRegistrationIdSchema.nullish().describe(
    "종사업장번호 (4 digits), only if the supplier has one."
  ),
  organizationName: z
    .string()
    .min(1)
    .max(70)
    .describe("Supplier's registered company name (상호명)."),
  representativeName: z
    .string()
    .min(1)
    .max(30)
    .describe("Supplier's representative name (대표자명)."),
  address: z.string().min(1).max(150).nullish().describe("Supplier address."),
  businessItem: z
    .string()
    .min(1)
    .max(100)
    .nullish()
    .describe("Business item (종목), e.g. 소프트웨어 개발 및 공급업."),
  businessType: z
    .string()
    .min(1)
    .max(100)
    .nullish()
    .describe("Business type (업태), e.g. 정보통신업."),
  manager: BoltaManagerSchema.describe("Supplier-side contact person."),
});
export type BoltaSupplierParty = z.infer<typeof BoltaSupplierPartySchema>;

export const BoltaRecipientPartySchema = z.object({
  identificationNumber: BoltaRecipientIdentificationNumberSchema.describe(
    "Recipient identification: 사업자등록번호 (10 digits) or " +
      "주민등록번호/외국인등록번호 (13 digits), no hyphens."
  ),
  taxRegistrationId: BoltaTaxRegistrationIdSchema.nullish().describe(
    "종사업장번호 (4 digits). Must be omitted when identificationNumber is " +
      "not a 사업자등록번호."
  ),
  organizationName: z
    .string()
    .min(1)
    .max(70)
    .describe("Recipient company or person name (상호명)."),
  representativeName: z
    .string()
    .min(1)
    .max(30)
    .describe("Recipient representative name (대표자명)."),
  address: z.string().min(1).max(150).nullish().describe("Recipient address."),
  businessItem: z.string().min(1).max(100).nullish().describe("종목."),
  businessType: z.string().min(1).max(100).nullish().describe("업태."),
  managers: z
    .array(BoltaManagerSchema)
    .min(1)
    .max(2)
    .describe(
      "Recipient-side contacts (1-2). The issued invoice is emailed to them."
    ),
});
export type BoltaRecipientParty = z.infer<typeof BoltaRecipientPartySchema>;

// Reverse issuance (역발행): the requesting party is the recipient and the
// supplier must be a business, so the identification is a 10-digit
// 사업자등록번호 only.
export const BoltaReverseRecipientPartySchema =
  BoltaRecipientPartySchema.extend({
    identificationNumber: BoltaBusinessRegistrationNumberSchema.describe(
      "Recipient's 사업자등록번호 (10 digits, no hyphens)."
    ),
  });

const boltaItemCommonShape = {
  date: BoltaDateStringSchema.describe("Supply date (공급일자, YYYY-MM-DD)."),
  name: z.string().min(1).max(100).describe("Item name (품목명)."),
  unitPrice: z
    .number()
    .int()
    .positive()
    .nullish()
    .describe("Unit price in KRW (단가), optional."),
  quantity: z.number().int().nullish().describe("Quantity (수량), optional."),
  specification: z
    .string()
    .min(1)
    .max(60)
    .nullish()
    .describe("Specification (규격), optional."),
  description: z
    .string()
    .min(1)
    .max(100)
    .nullish()
    .describe("Item remark (품목 비고), optional."),
};

export const BoltaInvoiceItemSchema = z.object({
  ...boltaItemCommonShape,
  supplyCost: z
    .number()
    .int()
    .min(1)
    .describe("Supply value in KRW (공급가액), positive integer."),
  tax: z
    .number()
    .int()
    .min(0)
    .nullish()
    .describe(
      "Tax amount in KRW (세액). Required non-null when taxType is TAXABLE " +
        "(usually 10% of supplyCost), must be 0 for ZERO_RATE, and must be " +
        "omitted for TAX_FREE."
    ),
});

// Amendment items express the delta versus the original invoice, so negative
// amounts are allowed.
export const BoltaAmendItemSchema = z.object({
  ...boltaItemCommonShape,
  supplyCost: z
    .number()
    .int()
    .describe(
      "Change in supply value in KRW (공급가액 변동분). Positive to increase, " +
        "negative to decrease."
    ),
  tax: z
    .number()
    .int()
    .nullish()
    .describe(
      "Change in tax amount in KRW (세액 변동분). Same sign as supplyCost " +
        "when taxType is TAXABLE, 0 for ZERO_RATE, omitted for TAX_FREE."
    ),
});

// --- API response schemas ---

export const BoltaErrorResponseSchema = z.object({
  code: z.string(),
  message: z.string(),
});
export type BoltaErrorResponse = z.infer<typeof BoltaErrorResponseSchema>;

export const BoltaIssueResponseSchema = z.object({
  issuanceKey: z.string(),
});

export const BoltaUrlResponseSchema = z.object({
  url: z.string(),
});

const BoltaCertificateSchema = z.object({
  issuedAt: z.string(),
  expiresAt: z.string(),
});

export const BoltaSupplierResponseSchema = z.object({
  supplierKey: z.string(),
  identificationNumber: z.string(),
  taxRegistrationId: z.string().nullish(),
  organizationName: z.string(),
  representativeName: z.string(),
  certificate: BoltaCertificateSchema.nullish(),
});
export type BoltaSupplierResponse = z.infer<typeof BoltaSupplierResponseSchema>;

export const BoltaTaxInvoiceDetailResponseSchema = z.object({
  issuanceKey: z.string(),
  ntsTransactionId: z.string(),
  issuedAt: z.string(),
  invoice: z.object({
    date: z.string(),
    purpose: BoltaPurposeSchema,
    supplier: z.unknown(),
    supplied: z.unknown(),
    items: z.array(z.unknown()),
    description: z.string().nullish(),
  }),
});

export const BoltaIssuanceStatusResponseSchema = z.object({
  issuanceKey: z.string(),
  clientReferenceId: z.string(),
  message: z.string(),
});
